import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    anyConstraintApplied,
    applyDrawingConstraintsToTexture,
} from '~/engine/tool/actions/contouring-actions/constraintClipping';
import { resolveDrawingConstraints } from '~/engine/tool/resolveDrawingConstraints';
import { CrossSectionTool } from '~/engine/tool/tools/contouring-tools/cross-section/CrossSectionTool';
import type { ToolManager } from '~/engine/tool/ToolManager';
import {
    reconstructFromSections,
    CrossSectionError,
    CrossSectionErrorCode,
    ReconstructionResult,
} from '~/engine/util/algorithms/sdf/cross-section';
import {
    GlOperationGuard,
    isAbortError,
} from '~/engine/util/algorithms/sdf/utils';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { sdfOperationInFlightState } from '~/state/atoms/structureSet/roiAtoms';
import {
    boundingRoiConstraintState,
    collisionConstraintState,
    crossSectionDrawingHistoryState,
    crossSectionPreviewHandleState,
    crossSectionPreviewReadyState,
    intensityThresholdConstraintState,
} from '~/state/atoms/toolsAtoms';
import { applyShapeToRoi } from './crossSectionApply';
import { usePreviewBase } from '~/hooks/structure-set/usePreviewBase';
import {
    getPreviewColor,
    PREVIEW_FILL_ALPHA,
} from '~/hooks/structure-set/previewColor';

const CROSS_SECTION_PREVIEW_KEY = 'cross-section-reconstruction';
const GL_WAIT_POLL_MS = 50;
const GL_WAIT_TIMEOUT_MS = 5000;

async function waitForIdleGl(): Promise<boolean> {
    const deadline = Date.now() + GL_WAIT_TIMEOUT_MS;
    while (GlOperationGuard.isActive) {
        if (Date.now() > deadline) {
            return false;
        }
        await new Promise((resolve) => setTimeout(resolve, GL_WAIT_POLL_MS));
    }
    return true;
}

/** The Cross-Section tool armed on `toolManager` when it targets `roi` and holds the contour set at `revision`. */
function activeToolHolding(
    toolManager: ToolManager | undefined,
    roi: RegionOfInterest,
    revision: number
): CrossSectionTool | null {
    const tool = toolManager?.getActiveTool();
    return tool instanceof CrossSectionTool &&
        tool.structure === roi &&
        tool.getDrawnRevision() === revision
        ? tool
        : null;
}

/** Localised message per error code; exhaustive so engine error text never reaches the clinician. */
export const CROSS_SECTION_ERROR_MESSAGE_IDS: Readonly<
    Record<CrossSectionErrorCode, string>
> = {
    'no-sections': 'crossSectionTool.error.noSections',
    'outside-image': 'crossSectionTool.error.outsideImage',
    'empty-result': 'crossSectionTool.error.emptyResult',
    'gl-busy': 'crossSectionTool.error.glBusy',
    'too-few-vertices': 'crossSectionTool.error.tooFewVertices',
    'degenerate-rank': 'crossSectionTool.error.degenerateRank',
    'invalid-parameter': 'crossSectionTool.error.invalidParameter',
};

/** Failures caused by the geometry just drawn, which take that contour back. */
const REJECTABLE_ERROR_CODES: ReadonlySet<CrossSectionErrorCode> = new Set([
    'too-few-vertices',
    'outside-image',
    'invalid-parameter',
    'degenerate-rank',
]);

const noNotices = () => ({ needSecond: false, split: false, looseFit: false });

/** Preview-and-apply flow for the Cross-Section (volumetric contour) tool. */
export function useCrossSectionPreview(): {
    recompute: () => Promise<void>;
    apply: () => Promise<void>;
} {
    const intl = useIntl();
    const { environment: activeEnvironment, roiId: selectedROI } =
        useActiveEditTarget();
    // The resolver yields `undefined`; the consumers below take `null`.
    const environment = activeEnvironment ?? null;
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    const boundingConstraint = useRecoilValue(boundingRoiConstraintState);
    const collisionConstraint = useRecoilValue(collisionConstraintState);
    const intensityThresholdConstraint = useRecoilValue(
        intensityThresholdConstraintState
    );
    const setIsProcessing = useSetRecoilState(sdfOperationInFlightState);
    const setPreviewReady = useSetRecoilState(crossSectionPreviewReadyState);
    const setPreviewHandle = useSetRecoilState(crossSectionPreviewHandleState);
    const preview = usePreviewBase({ environment });
    const { peekResult, resultRevision } = preview;
    useEffect(() => {
        const held = peekResult(CROSS_SECTION_PREVIEW_KEY);
        setPreviewHandle(held ? { ...held, revision: resultRevision } : null);
    }, [peekResult, resultRevision, setPreviewHandle]);
    useEffect(() => () => setPreviewHandle(null), [setPreviewHandle]);
    const previewConstraintKeyRef = useRef<string | null>(null);
    const previewRevisionRef = useRef<number | null>(null);
    // Each notice is given once per drawing and is due again once the drawing is empty.
    const announcedRef = useRef(noNotices());
    const { hasDrawing } = useRecoilValue(crossSectionDrawingHistoryState);
    useEffect(() => {
        if (!hasDrawing) {
            announcedRef.current = noNotices();
        }
    }, [hasDrawing]);
    const notify = useCallback(
        (
            id: string,
            variant: 'error' | 'warning' | 'success' | 'info',
            values?: Record<string, string | number>
        ) => enqueueSnackbar(intl.formatMessage({ id }, values), { variant }),
        [intl]
    );
    /** @param voxelMm Smallest voxel dimension; a sub-voxel deviation is not reported. */
    const announceReconstruction = useCallback(
        (built: ReconstructionResult, voxelMm: number) => {
            const announced = announcedRef.current;
            if (built.isSlab && !announced.needSecond) {
                announced.needSecond = true;
                notify('crossSectionTool.notice.needSecond', 'info');
            }
            if (built.connectivity.componentCount > 1 && !announced.split) {
                announced.split = true;
                notify('crossSectionTool.notice.split', 'warning', {
                    count: built.connectivity.componentCount,
                });
            }
            if (built.maxResidualMm > voxelMm && !announced.looseFit) {
                announced.looseFit = true;
                notify('crossSectionTool.notice.looseFit', 'warning', {
                    deviation: built.maxResidualMm.toFixed(1),
                });
            }
        },
        [notify]
    );
    const resolveConstraints = useCallback(
        () =>
            resolveDrawingConstraints(
                boundingConstraint,
                collisionConstraint,
                intensityThresholdConstraint,
                environment?.structureSet,
                environment?.image?.parent
            ),
        [
            boundingConstraint,
            collisionConstraint,
            intensityThresholdConstraint,
            environment,
        ]
    );
    /** A stable identity for the resolved constraints that shape the result. */
    const constraintKey = useCallback((): string => {
        const { constraints, boundingUnresolved, protectedUnresolved } =
            resolveConstraints();
        const protectedRois = (constraints.protectedStructures ?? [])
            .map((roi) => roi.roiId)
            .sort()
            .join(',');
        const threshold = constraints.intensityThreshold;
        return [
            constraints.bounding?.roiId ?? 'none',
            protectedRois,
            threshold ? `${threshold.rawLower}:${threshold.rawUpper}` : 'none',
            boundingUnresolved ?? 'none',
            protectedUnresolved,
        ].join('|');
    }, [resolveConstraints]);
    const constraintKeyRef = useRef(constraintKey);
    constraintKeyRef.current = constraintKey;
    const constraintsChangedSincePreview = useCallback(
        (): boolean =>
            previewConstraintKeyRef.current !== null &&
            previewConstraintKeyRef.current !== constraintKeyRef.current(),
        []
    );
    /** Clip the shape in place by the drawing modifiers, as a stroke is clipped at commit. */
    const applyConstraintsToShape = useCallback(
        (shape: ContourTexture, roi: RegionOfInterest, image: ImageObject) => {
            const applied = applyDrawingConstraintsToTexture(
                shape,
                image,
                resolveConstraints().constraints,
                roi.roiId
            );
            if (anyConstraintApplied(applied)) {
                shape.cropToContentBounds();
            }
        },
        [resolveConstraints]
    );
    const resolveTarget = useCallback(() => {
        const tool = toolManager?.getActiveTool();
        if (!(tool instanceof CrossSectionTool)) {
            return null;
        }
        const roi =
            selectedROI === null
                ? undefined
                : environment?.structureSet?.getRoi(selectedROI);
        // Compared by instance: the tool previews only onto the structure it was armed on.
        if (
            !roi ||
            !roi.canModifyContours ||
            !environment?.image ||
            tool.structure !== roi
        ) {
            return null;
        }
        return { tool, roi, crossSections: tool.getInputs() };
    }, [toolManager, environment, selectedROI]);
    const dropPreview = useCallback(
        (tool?: CrossSectionTool) => {
            preview.cleanup();
            tool?.setPreviewVisible(false);
            setPreviewReady(false);
            previewConstraintKeyRef.current = null;
            previewRevisionRef.current = null;
        },
        [preview, setPreviewReady]
    );
    const runPreview = useCallback(async () => {
        const target = resolveTarget();
        const image = environment?.image;
        if (!target || !image || target.crossSections.length === 0) {
            dropPreview(target?.tool);
            return;
        }
        const { tool, roi, crossSections } = target;
        const signal = preview.beginOperation();
        setPreviewReady(false);
        const revision = tool.getDrawnRevision();
        const superseded = (): boolean =>
            signal.aborted || tool.getDrawnRevision() !== revision;
        if (!(await waitForIdleGl())) {
            if (!signal.aborted) {
                dropPreview(tool);
                notify('crossSectionTool.error.glBusy', 'error');
            }
            return;
        }
        if (superseded()) {
            return;
        }
        // The shape is freed on every path unless handed to the preview slot.
        let shape: ContourTexture | undefined;
        let handedOff = false;
        try {
            const reconstruction = await reconstructFromSections(
                crossSections,
                image.parent.grid,
                { signal }
            );
            shape = reconstruction.texture;
            if (superseded()) {
                return;
            }
            applyConstraintsToShape(shape, roi, image);
            if (shape.isEmpty) {
                dropPreview(tool);
                notify('crossSectionTool.error.constrainedEmpty', 'warning');
                return;
            }
            previewConstraintKeyRef.current = constraintKey();
            previewRevisionRef.current = revision;
            const res = image.parent.grid.getRes();
            const voxelMm = Math.min(res[0], res[1], res[2]);
            announceReconstruction(reconstruction, voxelMm);
            const previewObj = new CustomTargetObject(
                image,
                shape,
                getPreviewColor(roi.color)
            );
            previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
            // The neighbouring structures are the anatomy being drawn against, so they keep their colours.
            previewObj.dimsOtherStructures = false;
            preview.setResult(previewObj, CROSS_SECTION_PREVIEW_KEY);
            handedOff = true;
            tool.setPreviewVisible(true);
            setPreviewReady(true);
        } catch (error) {
            if (signal.aborted || isAbortError(error)) {
                return;
            }
            // Asked before dropPreview, which aborts this pass's own controller.
            const wasSuperseded = superseded();
            dropPreview(tool);
            console.error('Cross-section preview error:', error);
            const rejected =
                error instanceof CrossSectionError &&
                REJECTABLE_ERROR_CODES.has(error.code) &&
                !wasSuperseded &&
                tool.rejectLastDrawing();
            const id =
                error instanceof CrossSectionError
                    ? CROSS_SECTION_ERROR_MESSAGE_IDS[error.code]
                    : 'crossSectionTool.computeFailed';
            if (rejected) {
                notify('crossSectionTool.error.refused', 'error', {
                    reason: intl.formatMessage({ id }),
                });
            } else {
                notify(id, 'error');
            }
        } finally {
            if (shape && !handedOff) {
                shape.delete();
            }
        }
    }, [
        resolveTarget,
        environment,
        applyConstraintsToShape,
        constraintKey,
        preview,
        dropPreview,
        setPreviewReady,
        announceReconstruction,
        notify,
        intl,
    ]);
    const runPreviewRef = useRef(runPreview);
    runPreviewRef.current = runPreview;
    const runApply = useCallback(async () => {
        const target = resolveTarget();
        if (!target) {
            notify('postProcessing.structureNotFound', 'error');
            return;
        }
        const { roi } = target;
        if (constraintsChangedSincePreview()) {
            notify('crossSectionTool.error.constraintsChanged', 'warning');
            await runPreviewRef.current();
            return;
        }
        if (!(await waitForIdleGl())) {
            notify('crossSectionTool.error.glBusy', 'error');
            return;
        }
        // Rechecked after the wait and before the texture is consumed: the modifiers or contours may have moved.
        if (constraintsChangedSincePreview()) {
            notify('crossSectionTool.error.constraintsChanged', 'warning');
            return;
        }
        const previewRevision = previewRevisionRef.current;
        if (previewRevision === null) {
            notify('crossSectionTool.error.nothingToApply', 'warning');
            return;
        }
        // Resolved again: the tool may have been re-armed onto the same contour set.
        const tool = activeToolHolding(toolManager, roi, previewRevision);
        if (!tool) {
            notify('crossSectionTool.error.contoursChanged', 'warning');
            return;
        }
        const texture = preview.consumeResult(CROSS_SECTION_PREVIEW_KEY);
        if (!texture) {
            notify('crossSectionTool.error.nothingToApply', 'warning');
            return;
        }
        tool.setPreviewVisible(false);
        setPreviewReady(false);
        setIsProcessing(true);
        let ownedByRoi = false;
        try {
            const revisionAtWrite = tool.getDrawnRevision();
            ownedByRoi = applyShapeToRoi(roi, texture);
            // Every contour is kept if they changed during the write or no tool holding them is active.
            const holder = activeToolHolding(toolManager, roi, revisionAtWrite);
            if (holder) {
                holder.clearAllCrossSections();
                holder.forgetDrawingHistory();
            }
            notify('crossSectionTool.applied', 'success', { name: roi.name });
            if (!holder) {
                notify(
                    'crossSectionTool.notice.contoursKeptAfterApply',
                    'warning'
                );
            }
        } catch (error) {
            console.error('Cross-section apply error:', error);
            notify('crossSectionTool.error.applyFailed', 'error');
            // The preview was consumed; rebuild it inline from the contours still drawn.
            await runPreviewRef.current();
        } finally {
            if (!ownedByRoi) {
                texture.delete();
            }
            setIsProcessing(false);
        }
    }, [
        resolveTarget,
        toolManager,
        constraintsChangedSincePreview,
        preview,
        setIsProcessing,
        setPreviewReady,
        notify,
    ]);
    const runApplyRef = useRef(runApply);
    runApplyRef.current = runApply;
    const chainRef = useRef<Promise<void>>(Promise.resolve());
    const recomputeQueuedRef = useRef(false);
    const disposedRef = useRef(false);
    useEffect(() => {
        disposedRef.current = false;
        return () => {
            disposedRef.current = true;
        };
    }, []);
    const enqueue = useCallback((job: () => Promise<void>): Promise<void> => {
        // A job queued on an unmounted instance must not touch a preview slot nobody will dispose.
        const run = (): Promise<void> =>
            disposedRef.current ? Promise.resolve() : job();
        const next = chainRef.current.then(run, run);
        chainRef.current = next;
        return next;
    }, []);
    const { abortInFlight } = preview;
    const recompute = useCallback((): Promise<void> => {
        abortInFlight();
        if (recomputeQueuedRef.current) {
            return chainRef.current;
        }
        recomputeQueuedRef.current = true;
        return enqueue(async () => {
            recomputeQueuedRef.current = false;
            await runPreviewRef.current();
        });
    }, [abortInFlight, enqueue]);
    const apply = useCallback(
        (): Promise<void> => enqueue(() => runApplyRef.current()),
        [enqueue]
    );
    // The preview follows every modifier, ROI selection and edit-target Environment change.
    useEffect(() => {
        void recompute();
    }, [
        recompute,
        boundingConstraint,
        collisionConstraint,
        intensityThresholdConstraint,
        selectedROI,
        environment,
    ]);
    return { recompute, apply };
}
